/**
 * Global Console Suppression for Production
 * Completely silences all console output in production environment
 */

const isProduction = () => {
  // Check multiple indicators of production environment
  return (
    process.env.NODE_ENV === 'production' ||
    process.env.REACT_APP_ENV === 'production' ||
    (typeof window !== 'undefined' && 
     window.location.hostname !== 'localhost' && 
     !window.location.hostname.includes('127.0.0.1') &&
     !window.location.hostname.includes('dev') &&
     !window.location.hostname.includes('staging'))
  );
};

// Store original console methods
const originalConsole = {
  log: console.log,
  error: console.error,
  warn: console.warn,
  info: console.info,
  debug: console.debug,
  trace: console.trace,
  table: console.table,
  group: console.group,
  groupEnd: console.groupEnd,
  groupCollapsed: console.groupCollapsed,
  time: console.time,
  timeEnd: console.timeEnd,
  assert: console.assert,
  count: console.count,
  countReset: console.countReset,
  dir: console.dir,
  dirxml: console.dirxml,
  clear: console.clear
};

// Silent no-op function
const noop = () => {};

/**
 * Suppress all console output in production
 */
export const suppressConsoleInProduction = () => {
  if (!isProduction()) {
    return; // Don't suppress in development
  }

  // Override all console methods with no-op functions
  console.log = noop;
  console.error = noop;
  console.warn = noop;
  console.info = noop;
  console.debug = noop;
  console.trace = noop;
  console.table = noop;
  console.group = noop;
  console.groupEnd = noop;
  console.groupCollapsed = noop;
  console.time = noop;
  console.timeEnd = noop;
  console.assert = noop;
  console.count = noop;
  console.countReset = noop;
  console.dir = noop;
  console.dirxml = noop;
  console.clear = noop;

  // Suppress unhandled promise rejections from showing in console
  window.addEventListener('unhandledrejection', (event) => {
    event.preventDefault();
    // Silently log to error tracking if needed
    sendToErrorTracking(event.reason);
  });

  // Suppress general errors from showing in console
  window.addEventListener('error', (event) => {
    event.preventDefault();
    // Silently log to error tracking if needed
    sendToErrorTracking(event.error);
  });
};

/**
 * Restore console methods (for debugging)
 */
export const restoreConsole = () => {
  console.log = originalConsole.log;
  console.error = originalConsole.error;
  console.warn = originalConsole.warn;
  console.info = originalConsole.info;
  console.debug = originalConsole.debug;
  console.trace = originalConsole.trace;
  console.table = originalConsole.table;
  console.group = originalConsole.group;
  console.groupEnd = originalConsole.groupEnd;
  console.groupCollapsed = originalConsole.groupCollapsed;
  console.time = originalConsole.time;
  console.timeEnd = originalConsole.timeEnd;
  console.assert = originalConsole.assert;
  console.count = originalConsole.count;
  console.countReset = originalConsole.countReset;
  console.dir = originalConsole.dir;
  console.dirxml = originalConsole.dirxml;
  console.clear = originalConsole.clear;
};

/**
 * Send errors to error tracking service (silently)
 */
const sendToErrorTracking = (error) => {
  try {
    // Only track critical errors in production
    if (!error || !isProduction()) return;

    // Example: Send to your backend error logging API
    // fetch('/api/log-error', {
    //   method: 'POST',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify({
    //     error: error?.message || String(error),
    //     stack: error?.stack,
    //     timestamp: new Date().toISOString(),
    //     url: window.location.href,
    //     userAgent: navigator.userAgent
    //   })
    // }).catch(() => {}); // Fail silently
  } catch (e) {
    // Fail silently - we don't want error tracking to cause issues
  }
};

// Expose restore function globally for emergency debugging
if (typeof window !== 'undefined') {
  window._restoreConsole = restoreConsole;
  window._suppressConsole = suppressConsoleInProduction;
}

export default {
  suppress: suppressConsoleInProduction,
  restore: restoreConsole,
  isProduction
};
